import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../../ui/global/Colors';
import Fonts from '../../../ui/global/Fonts';
import Button from '../../../ui/components/Button';
import Icon from '../../../ui/global/Icon';
import Tooltip from '../../../ui/components/Tooltip';

const ToolbarRoot = styled.div<{ $orientation: 'horizontal' | 'vertical' }>`
    position: relative;
    display: flex;
    flex-direction: row;
    bottom: 5px;
    gap: 10px;
    z-index: 10;
`;

const Toolbar = styled.div<{ $maxWidth: number }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: fit-content;
    max-width: ${props => props.$maxWidth}px;
    gap: 10px;
`;

const ToolbarSegment = styled.div<{ $orientation: 'horizontal' | 'vertical'; $isHidden?: boolean }>`
    display: ${props => props.$isHidden === true ? 'none' : 'flex'};
    ${props => props.$orientation === 'horizontal' ? `
        flex-direction: row;
        padding: 4px 6px;
        height: 42px;
    ` : `
        flex-direction: column;
        padding: 10px 6px;
    `}
    align-items: center;
    justify-content: center;
    background: ${Colors.white};
    border-radius: 4px;
    border: 1px solid ${Colors.grey1};
    gap: 12px;
    // box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1), 0 4px 2px rgba(0, 0, 0, 0.04);
`;

const Section = styled.div<{ $isHidden?: boolean }>`
    display: ${props => props.$isHidden === true ? 'none' : 'flex'};
    flex-direction: column;
    gap: 10px;
    position: relative;
`;

const SectionTitle = styled.div`
    font-size: 14px;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    color: ${Colors.grey6};
    text-align: center;
`;

const SectionInner = styled.div<{ $orientation: 'horizontal' | 'vertical' }>`
    ${props => props.$orientation === 'horizontal'
        ? `
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
        `
        : `
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            align-items: center;
            justify-items: center;
            
    `}
`;

const SectionInnerColor = styled(SectionInner)`
    ${props => props.$orientation === 'horizontal'
        ? `
            gap: 6px;
        `
        : `
            gap: 6px 20px;
            
    `}
`;

const SectionInnerThickness = styled(SectionInner)`
    gap: 6px;
`;

const SectionInnerTool = styled(SectionInner)`
    gap: 6px;
`;

const Divider = styled.div<{ $orientation: 'horizontal' | 'vertical'; $isHidden?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${Colors.grey1};
    ${props => props.$orientation === 'horizontal'
        ? `
            width: 1px;
            height: 24px;
        `
        : `
            height: 1px;
            width: 24px;
        `
    }
    opacity: ${props => props.$isHidden ? 0 : 1};
    margin: auto;
`;

const ColorButton = styled.button<{ $color: string, $isSelected: boolean, $orientation: 'horizontal' | 'vertical' }>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${props => props.$color};
    cursor: pointer;
    transition: transform 0.2s ease, border 0.2s ease, box-shadow 0.2s ease;
    
    ${props => props.$isSelected 
        ? `
            border: 2px solid white;
            box-shadow: 0 0 0 1px ${Colors.black};
        `
        : `
            border: 1px solid ${Colors.grey2};
        `
    }
    
    &:hover {
        border: 1px solid ${Colors.grey4};
        transform: scale(1.2);
    }
`;

const ThicknessButton = styled.button<{ $isSelected: boolean, $orientation: 'horizontal' | 'vertical' }>`
    width: 40px;
    min-width: 40px;
    height: 32px;
    border: 1px solid ${props => props.$isSelected ? Colors.black : Colors.transparent};
    background: ${props => props.$isSelected ? Colors.grey1 : Colors.white};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    
    &:hover {
        transform: scale(1.2);
    }
`;

const ThicknessIndicator = styled.div<{ $thickness: number, $color: string }>`
    width: 24px;
    min-width: 24px;
    height: ${props => props.$thickness}px;
    background: ${Colors.black};// ${props => props.$color};
    border-radius: ${props => props.$thickness}px;
    outline: 1px solid ${Colors.grey2};
`;

const ToolButton = styled.button<{ $isSelected: boolean, $orientation: 'horizontal' | 'vertical' }>`
    width: 40px;
    height: 32px;
    border: 1px solid ${props => props.$isSelected ? Colors.black : Colors.transparent};
    background: ${props => props.$isSelected ? Colors.grey1 : Colors.white};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    
    &:hover {
        transform: scale(1.2);
        // background: ${Colors.grayVeryPale};
    }
`;

const ResponsiveMenuButton = styled.button<{ $isOpen: boolean }>`
    width: 40px;
    height: 32px;
    border: 1px solid ${props => props.$isOpen ? Colors.black : Colors.transparent};
    background: ${props => props.$isOpen ? Colors.grey1 : Colors.white};
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
    
    &:hover {
        transform: scale(1.2);
        // background: ${Colors.grayVeryPale};
    }
`;

const ThicknessMenuIndicator = styled.div<{ $thickness: number }>`
  width: 40px;
  min-width: 32px;
  min-height: ${props => props.$thickness/2}px;
  background: ${Colors.black};
  border-radius: ${props => props.$thickness/2}px;
  border: 1px solid ${Colors.grey2};
`;

const ColorMenuIndicator = styled.div<{ $color: string }>`
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  border: 1px solid ${Colors.grey2};
`;

const ResponsiveMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  background: ${Colors.white};
  border-radius: 4px;
  border: 1px solid ${Colors.grey1};
  padding: 6px;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 6px;
  z-index: 100;
  // box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 5px;
  white-space: nowrap;
`;

const ResponsiveButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const ResponsiveThicknessInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const ResponsiveColorInner = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: center;
  justify-items: center;
  gap: 6px;
  min-width: max-content;
`;

const colors = {
    black: '#000000',
    grey: '#7f7f7f',
    white: '#FFFFFF',
    red: '#FF4848',
    orange: '#F99716',
    yellow: '#ffe640',
    green: '#4CAF50',
    aqua: '#20d3e3',
    blue: '#3867ff',
    purple: '#8f49f2',
    pink: '#fc5dba',
    pale: '#F9DCC4',
    tan: '#a87b56',
    brown: '#754f3e',
};

const thicknesses = { // in pixels
    thin: 4,
    medium: 12,
    thick: 24,
    extraThick: 40
};

export const tools = {
    select: 'cursor',
    draw: 'pencil',
    fill: 'paint-brush-broad',
    image: 'image',
};

interface ToolbarDrawProps {
    config: any;
    panelSize: number;
    selectedColor: string;
    selectedThickness: number;
    onColorChange: (color: string) => void;
    onThicknessChange: (thickness: number) => void;
    orientation: 'horizontal' | 'vertical';
    selectedTool: string;
    onToolChange: (tool: string) => void;
}

const ToolbarDraw: React.FC<ToolbarDrawProps> = ({
    config,
    panelSize,
    selectedColor,
    selectedThickness,
    onColorChange,
    onThicknessChange,
    orientation,
    selectedTool,
    onToolChange,
}) => {
    // State for responsive design
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [isThicknessMenuOpen, setIsThicknessMenuOpen] = useState(false);
    const [isColorMenuOpen, setIsColorMenuOpen] = useState(false);
    const [maxWidth, setMaxWidth] = useState(panelSize*2+10);
    const thicknessMenuRef = useRef<HTMLDivElement>(null);
    const colorMenuRef = useRef<HTMLDivElement>(null);
    
    // Check screen size on mount and resize
    useEffect(() => {
        const checkScreenSize = () => {
            setIsSmallScreen(window.innerWidth < 768);
        };
        
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        
        return () => {
            window.removeEventListener('resize', checkScreenSize);
        };
    }, []);

    // Set max width
    useEffect(() => {
        if (isSmallScreen) {
            setMaxWidth(Math.max(panelSize+10, window.innerWidth-10));
        } else {
            setMaxWidth(panelSize*2+10);
        }
    }, [isSmallScreen]);
    
    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (thicknessMenuRef.current && !thicknessMenuRef.current.contains(event.target as Node)) {
                setIsThicknessMenuOpen(false);
            }
            if (colorMenuRef.current && !colorMenuRef.current.contains(event.target as Node)) {
                setIsColorMenuOpen(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);
    
    // Render thickness and color controls based on screen size
    const renderThicknessAndColorControls = () => {
        if (isSmallScreen) {
            return (
                <ResponsiveButtonsContainer>
                    {/* Thickness Button and Menu */}
                    <Section ref={thicknessMenuRef}>
                        <ResponsiveMenuButton 
                            $isOpen={isThicknessMenuOpen}
                            onClick={() => {
                                setIsThicknessMenuOpen(!isThicknessMenuOpen);
                                setIsColorMenuOpen(false);
                            }}
                            title="Thickness"
                        >
                            <ThicknessMenuIndicator $thickness={selectedThickness} />
                        </ResponsiveMenuButton>
                        
                        <ResponsiveMenu $isOpen={isThicknessMenuOpen}>
                            <ResponsiveThicknessInner>
                                {Object.entries(thicknesses).map(([name, thickness], i, arr) => (
                                    <ThicknessButton
                                        key={name}
                                        $isSelected={selectedThickness === thickness}
                                        $orientation={orientation}
                                        onClick={() => {
                                            onThicknessChange(thickness);
                                            setIsThicknessMenuOpen(false);
                                        }}
                                        title={name}
                                    >
                                        <ThicknessIndicator $thickness={thickness/2} $color={selectedColor}/>
                                    </ThicknessButton>
                                )).reverse()}
                            </ResponsiveThicknessInner>
                        </ResponsiveMenu>
                    </Section>

                    <Divider $orientation={'horizontal'} $isHidden={selectedTool !== tools.draw} />
                    
                    {/* Color Button and Menu */}
                    <Section ref={colorMenuRef} >
                        <ResponsiveMenuButton 
                            $isOpen={isColorMenuOpen}
                            onClick={() => {
                                setIsColorMenuOpen(!isColorMenuOpen);
                                setIsThicknessMenuOpen(false);
                            }}
                            title="Color"
                        >
                            <ColorMenuIndicator $color={selectedColor} />
                        </ResponsiveMenuButton>
                        
                        <ResponsiveMenu $isOpen={isColorMenuOpen}>
                            <ResponsiveColorInner>
                                {Object.entries(colors).map(([name, color]) => (
                                    <ColorButton
                                        key={name}
                                        $color={color}
                                        $isSelected={selectedColor === color}
                                        $orientation={orientation}
                                        onClick={() => {
                                            onColorChange(color);
                                            setIsColorMenuOpen(false);
                                        }}
                                        title={name}
                                    />
                                ))}
                            </ResponsiveColorInner>
                        </ResponsiveMenu>
                    </Section>
                </ResponsiveButtonsContainer>
            );
        }
        
        // Regular desktop view
        return (
            <>
                <Section $isHidden={selectedTool !== tools.draw}>
                    <SectionInnerThickness $orientation={orientation}>
                        {Object.entries(thicknesses).map(([name, thickness]) => (
                            <ThicknessButton
                                key={name}
                                $isSelected={selectedThickness === thickness}
                                $orientation={orientation}
                                onClick={() => {
                                    onThicknessChange(thickness);
                                }}
                                title={name}
                            >
                                <ThicknessIndicator $thickness={thickness/2} $color={selectedColor}/>
                            </ThicknessButton>
                        ))}
                    </SectionInnerThickness>
                </Section>

                <Divider $orientation={orientation} $isHidden={selectedTool !== tools.draw} />

                <Section>
                    <SectionInnerColor $orientation={orientation}>
                    {Object.entries(colors).map(([name, color]) => (
                        <ColorButton
                            key={name}
                            $color={color}
                            $isSelected={selectedColor === color}
                            $orientation={orientation}
                            onClick={() => {
                                onColorChange(color);
                            }}
                            title={name}
                        />
                    ))}
                    </SectionInnerColor>
                </Section>
            </>
        );
    };

    return (
        <ToolbarRoot $orientation={orientation}>
            <Toolbar $maxWidth={maxWidth}>
            {config.SHOW_ADDITIONAL_ART_TOOLS && (
                <ToolbarSegment $orientation={orientation}>
                    <Section>
                        <SectionInnerTool $orientation={orientation}>
                            {Object.entries(tools).filter(([name, tool]) => tool !== tools.fill).map(([name, tool]) => (
                                <Tooltip
                                    key={name}
                                    text={name.charAt(0).toUpperCase() + name.slice(1)}
                                    colorMode="dark"
                                >
                                    <ToolButton
                                        key={name}
                                        $isSelected={selectedTool === tool}
                                        $orientation={orientation}
                                        onClick={() => {
                                            onToolChange(tool);
                                        }}
                                        title={name}
                                    >
                                        <Icon icon={tool} color={Colors.black} size={24} />
                                    </ToolButton>
                                </Tooltip>
                            ))}
                        </SectionInnerTool>
                    </Section>
                </ToolbarSegment>
            )}

            <ToolbarSegment $orientation={orientation} $isHidden={selectedTool !== tools.draw && selectedTool !== tools.fill}>
                {renderThicknessAndColorControls()}
            </ToolbarSegment>
            </Toolbar>
        </ToolbarRoot>
    );
};

export default ToolbarDraw;